import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as runtime from "react/jsx-runtime";
import { run } from "@mdx-js/mdx";
import { fetchBlogPost } from "../lib/api";
import { mdxComponents } from "../components/mdx";
import { PostReadingChrome, usePostHeadings } from "../components/PostReadingChrome";

export const Route = createFileRoute("/blog_/$slug")({
  component: BlogDetail,
});

// A compiled post's `compiledJs` (outputFormat: "function-body", see
// apps/api/src/services/mdx-compile.ts) references interactive components
// as `_components.GCVisualizer` etc, but never imports them — they're
// injected here as the real React implementation via the `components`
// prop @mdx-js/mdx's `run()` output accepts, per that service's own
// comment ("the frontend supplies the real React component via the
// `components` prop at render time").
type MDXContentComponent = (props: {
  components?: Record<string, React.ComponentType<any>>;
}) => React.ReactElement;

function useCompiledMdx(compiledJs: string | null | undefined) {
  const [Content, setContent] = React.useState<MDXContentComponent | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!compiledJs) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setError(null);
    run(compiledJs, { ...runtime, baseUrl: import.meta.url })
      .then((mod) => {
        if (!cancelled) setContent(() => mod.default as MDXContentComponent);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [compiledJs]);

  return { Content, error };
}

// Delegated to the wrapping post-body div (rather than one listener per
// footnote ref) so it works for however many `.fn` elements the compiled
// MDX contains — see apps/api/src/services/mdx-footnotes.ts, which emits
// plain `<a class="fn" href="#fnN">` + `<div class="fn-content" id="fnN">`
// for any `[^n]` footnote syntax in a post's source. Click toggles the
// matching fn-content's visibility in place instead of letting the anchor
// navigate to it, mirroring design-reference/blog-gc-post.html's
// `document.getElementById(fn.dataset.fn).classList.toggle('open')`.
function handlePostBodyClick(e: React.MouseEvent<HTMLDivElement>) {
  const target = (e.target as HTMLElement).closest<HTMLElement>(".fn");
  if (!target) return;
  e.preventDefault();
  const id = target.getAttribute("data-fn") ?? target.getAttribute("href")?.replace(/^#/, "");
  if (id) document.getElementById(id)?.classList.toggle("open");
}

function BlogDetail() {
  const { slug } = Route.useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog", slug],
    queryFn: () => fetchBlogPost(slug),
  });

  const { Content, error } = useCompiledMdx(data?.compiledJs);
  const postBodyRef = React.useRef<HTMLDivElement>(null);
  const headings = usePostHeadings(postBodyRef, Boolean(Content));

  return (
    <>
      <PostReadingChrome headings={headings} />

      <p className="frame-label">
        frame <b>#4</b> — blog()
      </p>

      {isLoading && <p className="section-sub">// loading...</p>}
      {isError && <p className="section-sub">// could not reach the api</p>}

      {data && (
        <>
          <p className="post-meta">
            {data.publishedAt ? data.publishedAt.slice(0, 10) : "unpublished"} ·{" "}
            {data.readTime ? `${data.readTime} min read` : ""}
            {data.tags.length > 0 ? ` · ${data.tags.join(", ")}` : ""}
          </p>
          <h1>{data.title}</h1>
          {data.dek && <p className="dek">{data.dek}</p>}

          <div className="post-body" ref={postBodyRef} onClick={handlePostBodyClick}>
            {error && <p className="section-sub">// failed to render post content: {error}</p>}
            {!error && Content && <Content components={mdxComponents} />}
            {!error && !Content && !isLoading && (
              <p className="section-sub">// no content compiled for this post yet.</p>
            )}
          </div>
        </>
      )}

      <Link to="/blog" className="see-all">
        ← back to compiled_artifacts[]
      </Link>
    </>
  );
}
